/* ============================================================
   PHREE SKIN — motion (Seed-caliber, GSAP + ScrollTrigger + Lenis).
   Discipline over engine: two durations, one house curve, 0.05s
   stagger, low-bounce overshoot. Respects reduced-motion.
   ============================================================ */
(function () {
  "use strict";
  var reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var FAST = 0.3, MID = 0.62, RISE = 0.7, STAG = 0.05;

  function splitInto(el, unit) {
    var parts = el.innerHTML.split(/(<br\s*\/?>)/i);
    el.innerHTML = parts.map(function (p) {
      if (/<br/i.test(p)) return p;
      if (unit === "letter") {
        return p.replace(/\S/g, function (ch) { return '<span class="wln"><span class="wln__i">' + ch + "</span></span>"; });
      }
      return p.split(/\s+/).filter(Boolean).map(function (w) {
        return '<span class="wln"><span class="wln__i">' + w + "</span></span>";
      }).join(" ");
    }).join("");
    return el.querySelectorAll(".wln__i");
  }

  if (reduce || !window.gsap) {
    document.body.classList.add("no-motion");
    document.querySelectorAll("[data-reveal]").forEach(function (e) { e.style.opacity = 1; e.style.transform = "none"; });
    return;
  }

  /* Lenis smooth scroll */
  var lenis = null;
  if (window.Lenis) {
    lenis = new Lenis({ lerp: 0.11, wheelMultiplier: 1 });
    /* exposed so the mobile menu and the cart drawer can actually stop the page;
       body{overflow:hidden} does nothing while Lenis is driving the scroll */
    window.__phreeLenis = lenis;
    document.documentElement.classList.add("lenis");
    requestAnimationFrame(function raf(t) { lenis.raf(t); requestAnimationFrame(raf); });
  }
  gsap.registerPlugin(ScrollTrigger);
  if (lenis) lenis.on("scroll", ScrollTrigger.update);

  /* reveals — house-curve rise (power3.out ≈ (.75,0,.25,1) out-phase) */
  gsap.utils.toArray("[data-reveal]").forEach(function (el) {
    gsap.to(el, {
      opacity: 1, y: 0, duration: MID, ease: "power3.out", delay: +el.dataset.delay || 0,
      scrollTrigger: { trigger: el, start: "top 86%", once: true }
    });
  });

  /* hero split-word rise — 700ms, slight overshoot, 0.05s stagger.
     In-view headlines play immediately (reliable above the fold); others on scroll. */
  gsap.utils.toArray("[data-splitwords]").forEach(function (el) {
    var words = splitInto(el, "word");
    gsap.set(words, { yPercent: 115 });
    var base = { yPercent: 0, duration: RISE, ease: "power4.out", stagger: STAG };
    var played = false;
    function play() { if (played) return; played = true; gsap.to(words, base); }
    if (el.getBoundingClientRect().top < window.innerHeight * 0.92) {
      base.delay = 0.2; play();
    } else {
      /* IntersectionObserver is more reliable than ScrollTrigger for injected /
         far-below-the-fold headlines (the footer wordmark got stuck hidden). */
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (en) { if (en.isIntersecting) { play(); io.disconnect(); } });
      }, { threshold: 0.2, rootMargin: "0px 0px -6% 0px" });
      io.observe(el);
      /* last-ditch: never leave the headline hidden */
      setTimeout(function () { var r = el.getBoundingClientRect(); if (!played && r.top < window.innerHeight && r.bottom > 0) play(); }, 2500);
    }
  });

  /* per-letter reveal (eyebrows / small accents) */
  gsap.utils.toArray("[data-splitletters]").forEach(function (el) {
    var letters = splitInto(el, "letter");
    gsap.set(letters, { yPercent: 110, opacity: 0 });
    gsap.to(letters, {
      yPercent: 0, opacity: 1, duration: 0.5, ease: "power3.out", stagger: 0.02,
      scrollTrigger: { trigger: el, start: "top 90%", once: true }
    });
  });

  /* lift-cards — Seed's signature: panel scaleY .92→1 + media scale→1.14.
     Skip any card a page script already owns (e.g. home.js drives the
     two-product cards with the exact ::before-panel + media recipe). */
  gsap.utils.toArray("[data-lift]:not([data-lift-owned])").forEach(function (card) {
    var media = card.querySelector(".lift__media");
    gsap.set(card, { transformOrigin: "50% 100%" });
    ScrollTrigger.create({
      trigger: card, start: "top 86%", once: true, onEnter: function () {
        gsap.fromTo(card, { scaleY: 0.92, opacity: 0 }, { scaleY: 1, opacity: 1, duration: 0.5, ease: "power3.out" });
        if (media) gsap.fromTo(media, { scale: 1.0 }, { scale: 1.14, duration: 0.65, ease: "power3.out" });
      }
    });
  });

  /* staggered grids — children lift in sequence */
  gsap.utils.toArray("[data-stagger]").forEach(function (grid) {
    var kids = grid.children;
    gsap.set(kids, { opacity: 0, y: 26 });
    ScrollTrigger.create({
      trigger: grid, start: "top 82%", once: true, onEnter: function () {
        gsap.to(kids, { opacity: 1, y: 0, duration: MID, ease: "power3.out", stagger: STAG });
      }
    });
  });

  /* Parallax layers.
     This was the last scroll-SCRUBBED effect in the theme: layer position bound directly to scroll
     offset, so it only moved while the visitor moved, and a still visitor saw a still frame. The
     owner's rule is that nothing load-bearing may depend on scrolling. Parallax is decorative depth
     rather than content, so instead of deleting it we give it its own clock: a slow, continuous
     drift that reads as depth whether or not anyone scrolls, and costs nothing when off-screen.
     Set data-parallax-scrub on an element to opt that one layer back into the old behaviour. */
  gsap.utils.toArray("[data-parallax]").forEach(function (el) {
    var amt = +el.dataset.parallax || -12;
    if (el.hasAttribute("data-parallax-scrub")) {
      gsap.to(el, {
        yPercent: amt, ease: "none",
        scrollTrigger: { trigger: el.closest("[data-parallax-scope]") || el, start: "top bottom", end: "bottom top", scrub: true }
      });
      return;
    }
    var drift = gsap.to(el, {
      yPercent: amt * 0.5, duration: 9, ease: "sine.inOut",
      yoyo: true, repeat: -1, paused: true
    });
    /* only animate while it is on screen — a permanently running tween off-screen is wasted work */
    ScrollTrigger.create({
      trigger: el.closest("[data-parallax-scope]") || el,
      start: "top bottom", end: "bottom top",
      onEnter: function () { drift.play(); },
      onEnterBack: function () { drift.play(); },
      onLeave: function () { drift.pause(); },
      onLeaveBack: function () { drift.pause(); }
    });
    drift.play();
  });

  /* stat count-up */
  gsap.utils.toArray("[data-count]").forEach(function (el) {
    var to = parseFloat(el.dataset.count), suf = el.dataset.suffix || "";
    ScrollTrigger.create({
      trigger: el, start: "top 90%", once: true, onEnter: function () {
        var o = { v: 0 };
        gsap.to(o, { v: to, duration: 1.4, ease: "power1.out", onUpdate: function () { el.textContent = Math.round(o.v) + suf; } });
      }
    });
  });

  /* safety net — never leave in-view content hidden if rAF stalls (bg tab / throttling) */
  setTimeout(function () {
    document.querySelectorAll("[data-reveal], .wln__i").forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > -40) { gsap.killTweensOf(el); gsap.set(el, { opacity: 1, y: 0, yPercent: 0 }); }
    });
    document.querySelectorAll("[data-lift] .lift__media").forEach(function (m) {
      var r = m.getBoundingClientRect(); if (r.top < window.innerHeight && r.bottom > 0 && getComputedStyle(m).transform === "none") gsap.set(m, { scale: 1.06 });
    });
    ScrollTrigger.refresh();
  }, 1500);

  window.addEventListener("load", function () { ScrollTrigger.refresh(); });
})();
